import { api, handleApiError } from '../client'

// Types
export type OnboardingStatus = 'InProgress' | 'Completed' | 'Skipped' | 'Paused'
export type AccountType = 'Personal' | 'Team' | 'Enterprise'

export interface CompletedStep {
  stepName: string
  stepNumber: number
  completedAt: string
  notes?: string
  data?: Record<string, unknown>
}

export interface OnboardingProgress {
  id: string
  tenantId: string
  userId: string
  currentStep: string
  currentStepNumber: number
  totalSteps: number
  status: OnboardingStatus
  accountType: AccountType
  selectedPresetId?: string
  includeDefaultAgents: boolean
  selectedTier?: string
  selectedBillingCycle?: string
  trialEnabled: boolean
  completedSteps: CompletedStep[]
  completionPercentage: number
  startedAt: string
  completedAt?: string
  lastActivityAt?: string
  createdAt: string
  updatedAt: string
}

export interface OnboardingCompletionResult {
  progress: OnboardingProgress
  resourcesCreated: boolean
  appliedPresetId?: string
  createdAgentIds: string[]
  subscriptionId?: string
  welcomeEmailSent: boolean
  warnings: string[]
}

export interface OnboardingStatusResponse {
  hasStarted: boolean
  isComplete: boolean
  currentStep?: string
  completionPercentage?: number
}

export interface StartOnboardingRequest {
  accountType: AccountType
}

export interface CompleteStepRequest {
  stepName: string
  data?: Record<string, unknown>
  notes?: string
}

export interface SkipOnboardingRequest {
  applyDefaults?: boolean
}

// Step definitions
export const ONBOARDING_STEPS = {
  ACCOUNT_TYPE: 'account-type',
  USER_PROFILE: 'user-profile',
  WORKSPACE_SETUP: 'workspace-setup',
  SUBSCRIPTION: 'subscription',
  COMPLETION: 'completion'
} as const

export const STEP_CONFIG = [
  {
    name: ONBOARDING_STEPS.ACCOUNT_TYPE,
    title: 'Account Type',
    description: 'Choose how you\'ll be using Templatean'
  },
  {
    name: ONBOARDING_STEPS.USER_PROFILE,
    title: 'Your Profile',
    description: 'Tell us a bit about yourself'
  },
  {
    name: ONBOARDING_STEPS.WORKSPACE_SETUP,
    title: 'Workspace Setup',
    description: 'Configure your workspace with presets or start fresh'
  },
  {
    name: ONBOARDING_STEPS.SUBSCRIPTION,
    title: 'Choose Plan',
    description: 'Select a plan that fits your needs'
  },
  {
    name: ONBOARDING_STEPS.COMPLETION,
    title: 'Get Started',
    description: 'You\'re all set to begin!'
  }
]

// API Service
export const onboardingService = {
  /**
   * Get current onboarding progress
   */
  async getProgress(): Promise<OnboardingProgress | null> {
    try {
      const response = await api.get<OnboardingProgress>('/v1/onboarding')
      return response.data
    } catch (error: unknown) {
      // 204 No Content means no onboarding started
      if ((error as { response?: { status: number } })?.response?.status === 204) {
        return null
      }
      throw handleApiError(error)
    }
  },

  /**
   * Get onboarding status (quick check)
   */
  async getStatus(): Promise<OnboardingStatusResponse> {
    const response = await api.get<OnboardingStatusResponse>('/v1/onboarding/status')
    return response.data
  },

  /**
   * Start or resume onboarding
   */
  async start(accountType: AccountType): Promise<OnboardingProgress> {
    const response = await api.post<OnboardingProgress>('/v1/onboarding/start', { accountType })
    return response.data
  },

  /**
   * Complete current step with data
   */
  async completeStep(request: CompleteStepRequest): Promise<OnboardingProgress> {
    const response = await api.post<OnboardingProgress>('/v1/onboarding/complete-step', request)
    return response.data
  },

  /**
   * Complete the entire onboarding process
   */
  async complete(): Promise<OnboardingCompletionResult> {
    const response = await api.post<OnboardingCompletionResult>('/v1/onboarding/complete')
    return response.data
  },

  /**
   * Skip onboarding with optional smart defaults
   */
  async skip(applyDefaults = true): Promise<OnboardingCompletionResult> {
    const response = await api.post<OnboardingCompletionResult>('/v1/onboarding/skip', { applyDefaults })
    return response.data
  }
}

export default onboardingService
